import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig, CancelTokenSource, AxiosResponse } from 'axios'
import Store from '@/store'
import router from '@/router'
import { isSessionExpired, touchSessionTime } from '@/utils/sessionTimeout'
import { analytics } from '@/plugins/analytics'

export type ApiConfig = {
  token?: string
}

export type AxiosInstanceConfig = {
  baseURL: string
  headers?: { [key: string]: string | number | object | null | undefined | void }
  [key: string]: string | number | object | null | undefined | void
}

const SEC = 1000

/**
 * 인증 만료 로그아웃 이동이 **진행 중**인지 — 모듈 수준이라 모든 Api 인스턴스가 같이 본다(Astra r4).
 * 동시에 온 401 은 이동 하나로 모으고, 이동이 끝나면(성공·실패·취소 무관) 다시 연다 — 이탈 확인으로 취소되면 다음 401 이 다시 시도한다.
 * 영구 플래그·시간 창을 두지 않는다: 이미 로그아웃된 뒤의 늦은 401 은 '로그인돼 있음'(localStorage 토큰)으로 거른다.
 */
let authLogoutPending = false

abstract class Api {
  public token: string | null | undefined
  public domain: string
  public axios: AxiosInstance
  public TIME_OUT = SEC * 30
  public store = Store
  public cancelToken: CancelTokenSource | null = null

  constructor({ baseURL, token, ...configs }: ApiConfig & AxiosInstanceConfig) {
    this.domain = baseURL
    this.token = token || Store.getters.token || localStorage.getItem('accessToken')

    //if (this.domain.includes('mdm.selpi.co.kr')) {
    //console.log(token, '>', Store.getters.token, '>', localStorage.getItem('accessToken'))
    //}
    const config = {
      headers: { 'Service-No': this.store.getters.serviceNo },
      ...configs,
    }

    if (this.token) {
      config['headers']['authorization'] = this.token
    }

    // admin-api 공통처리
    if (this.domain.includes('https://admin-api')) {
      config['headers']['referer-type'] = 'new-admin'
    }
    // order-api 공통처리
    if (this.domain.includes('https://order-api') || this.domain.includes('localhost:8080')) {
      config['headers']['Seller-No'] = this.store.getters.sellerNo || localStorage.getItem('sellerNo')
    }

    this.axios = Axios.create({
      baseURL: this.domain,
      timeout: this.TIME_OUT,
      ...config,
    })

    this.axios.interceptors.response.use(this.onResponse.bind(this), this.onError.bind(this))
    this.axios.interceptors.request.use(this.onRequest.bind(this), undefined)
  }

  onServerErrorHandler(error: AxiosError<Common.TApiResponseWrapper>) {
    // `error.code` 봉투가 아닌 응답(adminapi `{ meta }` 등)의 HTTP 401 — 인증 만료(Astra 2026-10-01 r2 MAJOR B).
    // 예전엔 아래 분기에 들어가지 못해 아무 처리도 없이 만료된 토큰으로 화면에 남았다. HTTP 403 은 권한 없음이라 넣지 않는다
    if (!error.response?.data?.error?.code && error.response?.status === 401) {
      this.onAuthError(error)
      return
    }
    if (error.response && error.response.data?.error?.code) {
      switch (error.response.data.error.code) {
        // 인증만료 or 토큰 유효성 검사 실패
        case 401:
        case 403:
          this.onAuthError(error)
          break
        case 503:
          this.store.commit('setMaintenanceText', { text: error.response.data.error.message, isMaintenance: true })
          break
        case 500:
        case 400:
          // order-api 에러 공통처리
          // admin-api 에러 공통처리
          // * 그 외 API 는 에러처리 별도 구현 필요
          if (
            error.request?.responseURL.includes('https://order-api') ||
            error.request?.responseURL.includes('https://admin-api') ||
            error.request?.responseURL.includes('https://seller-api')
          ) {
            this.store.commit('closeErrorBanner')
            this.store.commit('endLoading')
            this.store.commit('showSnackbar', { text: error.response.data.error.message, type: 'error' })
          }
          break
        case 404:
          this.store.commit('endLoading')
          this.store.commit('openErrorBanner', { text: error.response.data.error.message, type: 'error' })
          break
      }
    }
  }

  /**
   * 인증 오류의 갈래 — 'logout' 이면 로그아웃한다. 아니면 null.
   * 인증 오류 = 본문 `error.code` 401·403(v3·order·seller 봉투) 또는 **HTTP 상태 401**(adminapi `{ meta }` 등 — r2 MAJOR B).
   * HTTP 403 은 넣지 않는다 — adminapi 의 403 은 권한 없음(메뉴 권한·남의 셀러 리소스)이라 로그아웃시키면 안 된다.
   * MDM 토큰 만료(401)는 재갱신 대상('mdm'), 발할라(정산)는 독립 인증 체계라('valhalla') 로그아웃하지 않는다(이 순서 그대로).
   */
  authErrorKind(error: AxiosError<Common.TApiResponseWrapper>): 'logout' | 'mdm' | 'valhalla' | null {
    const code = error.response?.data?.error?.code
    const unauthorized = code === 401 || error.response?.status === 401
    if (!unauthorized && code !== 403) return null
    if (unauthorized && error?.config?.baseURL === process.env.VUE_APP_SERVER_ENDPOINT_MDM) return 'mdm'
    if (error.request?.responseURL?.includes(process.env.VUE_APP_SERVER_ENDPOINT_VALHALLA || 'valhalla-api')) return 'valhalla'
    return 'logout'
  }

  /** 인증 오류 공통 처리 — 발할라는 알림만, 로그아웃 갈래는 로그아웃, MDM 은 아무것도 하지 않는다(재갱신 대상) */
  onAuthError(error: AxiosError<Common.TApiResponseWrapper>) {
    const kind = this.authErrorKind(error)
    // 발할라(정산) API는 독립 인증 체계이므로 로그아웃 처리하지 않고 알림만 표시
    if (kind === 'valhalla') {
      this.store.commit('endLoading')
      this.store.commit('showSnackbar', { text: '정산 서비스 요청에 실패했습니다. 잠시 후 다시 시도해주세요. 문제가 지속되면 관리자에게 문의해주세요.', type: 'error' })
    } else if (kind === 'logout') {
      this.logoutOnAuthError()
    }
    // 'mdm' — MDM token 인증만료의 경우 재갱신 처리 되어야 함(여기서는 아무것도 하지 않는다)
  }

  /**
   * 인증 만료 — 로그아웃 화면으로 보내고 안내한다(공통 처리·`silentError` 요청·`error.code` 401/403·HTTP 401 공통).
   * ⓐ 이미 로그아웃된 상태(로그인 표식 = `localStorage.accessToken` — 일반·관리자·SSO 콜백 로그인이 모두 남기고 새로고침에도 남으며
   *    `Logout.vue` 가 지운다. 스토어 토큰은 SSO 콜백·새로고침 복원 전에는 비어 있어 표식이 못 된다)이거나 이미 로그아웃·로그인 화면이면 아무것도 하지 않는다.
   * ⓑ 로그아웃 이동이 진행 중이면 건너뛴다(동시 401 은 한 번). ⓒ 아니면 안내 1회 → 이동, 이동이 끝나면(성공·실패·취소) 다시 연다.
   * 문구가 다른 API(`SellerProductsApi`)는 `authExpiredText` 만 바꾼다 — 먼저 온 쪽 문구가 한 번 나간다
   */
  logoutOnAuthError() {
    if (!localStorage.getItem('accessToken')) return
    const path = router.currentRoute?.value?.path
    if (path === '/logout' || path === '/login') return
    if (authLogoutPending) return
    authLogoutPending = true
    analytics.endAuth('auth_error')
    this.store.commit('showSnackbar', { text: this.authExpiredText() })
    // push 호출 자체가 동기로 던져도 진행 중 표시가 남지 않게 — 그 경우 이동은 끝난 것으로 보고 바로 다시 연다(Astra r5).
    // 안내 → 이동 순서는 그대로 같은 틱에 둔다(체인 안으로 넣지 않는다)
    let navigation: Promise<unknown> = Promise.resolve()
    try {
      navigation = Promise.resolve(router.push('/logout'))
    } catch (error) {
      // 동기 throw — 아래 finally 가 진행 중 표시를 푼다. 다음 401 이 다시 시도한다
    }
    navigation
      .catch(() => undefined) // 이동 실패·취소 — 다음 401 이 다시 시도한다(이탈 확인을 우회하지 않는다)
      .finally(() => {
        authLogoutPending = false
      })
  }

  /** 인증 만료 안내 문구 */
  authExpiredText(): string {
    return '인증이 만료되었습니다. 로그인을 해주세요.'
  }

  onError(error: AxiosError<Common.TApiResponseWrapper>) {
    analytics.reportApiError(error) // 계측 전용 관찰 — 취소·세션만료는 내부에서 제외
    // 백그라운드 보조 조회(이행률·쿠폰 성과 등) — 실패해도 본문 화면·전역 배너에 관여하지 않는다.
    // 알림(배너·스낵바·로딩 해제)만 호출부에 맡기고, **인증 만료는 다른 요청과 똑같이 로그아웃까지** 처리한다 —
    // 예전엔 여기서 바로 돌아가 만료된 토큰으로 화면에 남았다(Astra 2026-10-01 MAJOR 2)
    if (error.config && (error.config as { silentError?: boolean }).silentError) {
      if (!Axios.isCancel(error) && this.authErrorKind(error) === 'logout') this.logoutOnAuthError()
      return Promise.reject(error)
    }
    this.handleError(error)
    return Promise.reject(error)
  }

  /**
   * 공통 오류 처리(로딩 해제·스낵바·배너·인증 만료) — `onError` 가 부르는 본체.
   * `silentError` 로 미뤄 둔 호출부가 스스로 판단해(예: 최신 검색의 실패일 때만) 나중에 부를 수 있다.
   * 계측(`reportApiError`)은 `onError` 가 이미 했으므로 여기서 하지 않는다.
   */
  handleError(error: AxiosError<Common.TApiResponseWrapper>) {
    switch (true) {
      case Axios.isCancel(error): // Axios 중복 요청 에러 처리
        return
      case !!error.response: // 서버 에러 처리
        this.onServerErrorHandler(error)
        return
      default:
        this.store.commit('endLoading')
        this.store.commit('setMaintenanceText', { text: null, isMaintenance: false })
        this.store.commit('openErrorBanner', error.response?.data?.error?.message || error)
    }
  }

  onParamsEscape(params: URLSearchParams) {
    const URL = new URLSearchParams()

    for (const key in params) {
      if (params[key] !== null && params[key] !== undefined) {
        URL.append(key, params[key])
      }
    }

    return URL
  }

  onQueryStringEscape(url: string) {
    if (url.includes('?')) {
      const [baseUrl, queryString] = url.split('?')
      const URL = new URLSearchParams(queryString)

      for (const key of URL.keys()) {
        if (URL.get(key) === '' || URL.get(key) === null || URL.get(key) === undefined) {
          URL.delete(key)
        }
      }

      return baseUrl + (URL.toString() && `?${URL.toString()}`)
    }

    return url
  }

  onRequest(config: AxiosRequestConfig) {
    analytics.markRequest(config) // GA4: 요청 시작 컨텍스트 기억 (config 미변경)
    // 무활동 세션 타임아웃 초과 시 로그아웃 (정책: src/utils/sessionTimeout.ts)
    if (isSessionExpired(this.store.getters.isAdmin)) {
      analytics.endAuth('session_expired')
      router.push('/logout')
      this.store.commit('showSnackbar', { text: '인증이 만료되었습니다. 로그인을 해주세요.' })
      localStorage.removeItem('sessionTime')

      return Promise.reject()
    }

    // localStorage에 sessionTime 저장 (마지막 요청 시각 갱신) — 로그인 상태의 보호 화면에서만 (정책: src/utils/sessionTimeout.ts)
    touchSessionTime()

    // 호출 API 공통처리(URL escape)
    config.params = this.onParamsEscape(config.params)
    config.url = this.onQueryStringEscape(config.url as string)

    return config
  }

  onResponse(response: AxiosResponse) {
    console.log(`%c[Response API URL(${response?.config?.method?.toUpperCase()})] `, 'font-weight:bold;color:skyblue;', response.config.url)
    console.log(`%c[Response body] `, 'font-weight:bold;color:skyblue;', response.data)
    return Promise.resolve(response)
  }

  console(API, requestType, payload?) {
    if (process.env.NODE_ENV === 'production') return
    return (
      console.log(`%c[Request API URL(${requestType ?? '-'})] `, 'font-weight:bold;', API),
      payload && console.log(`%c[PAYPLOAD] `, 'font-weight:bold;', payload)
    )
  }

  /**
   * GET 요청
   * @param URL
   * @param params (not required)
   * @param configs (not required)
   */
  requestGet<T>(URL: string, params?: object, configs?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // 중복 요청 취소
    if (this.cancelToken) {
      this.cancelToken.cancel()
    }

    this.cancelToken = Axios.CancelToken.source()
    this.console(URL, 'GET', params)

    return this.axios.get(URL, {
      ...configs,
      params: params,
      cancelToken: this.cancelToken.token,
    })
  }

  /**
   * POST 요청
   * @param URL
   * @param data (body) (not required)
   * @param configs (not required)
   */
  requestPost<T>(URL: string, data?: object, configs?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // 중복 요청 취소
    if (this.cancelToken) {
      this.cancelToken.cancel()
    }

    this.cancelToken = Axios.CancelToken.source()
    this.console(URL, 'POST', data)

    return this.axios.post(URL, data, {
      ...configs,
      cancelToken: this.cancelToken.token,
    })
  }

  /**
   * PUT 요청
   * @param URL
   * @param data (body) (not required)
   * @param configs (not required)
   */
  requestPut<T>(URL: string, data?: object, configs?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // 중복 요청 취소
    if (this.cancelToken) {
      this.cancelToken.cancel()
    }

    this.cancelToken = Axios.CancelToken.source()
    this.console(URL, 'PUT', data)

    return this.axios.put(URL, data, {
      ...configs,
      cancelToken: this.cancelToken.token,
    })
  }

  /**
   * PATCH 요청
   * @param URL
   * @param data (body) (not required)
   * @param configs (not required)
   */
  requestPatch<T>(URL: string, data?: object, configs?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // 중복 요청 취소
    if (this.cancelToken) {
      this.cancelToken.cancel()
    }

    this.cancelToken = Axios.CancelToken.source()
    this.console(URL, 'PATCH', data)

    return this.axios.patch(URL, data, {
      ...configs,
      cancelToken: this.cancelToken.token,
    })
  }

  /**
   * DELETE 요청
   * @param URL
   * @param configs (not required)
   */
  requestDelete<T>(URL: string, configs?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // 중복 요청 취소
    if (this.cancelToken) {
      this.cancelToken.cancel()
    }

    this.cancelToken = Axios.CancelToken.source()
    this.console(URL, 'DELETE')

    return this.axios.delete(URL, {
      ...configs,
      cancelToken: this.cancelToken.token,
    })
  }
}

export default Api
